import { useEffect, useMemo, useState,useContext } from "react";
import axios from "axios";
import Input from "../ui/Input";
import Select from "../ui/Select";
import Badge from "../ui/Badge";
import { tl } from "../../utils/format";
import { useParams, useHistory } from "react-router-dom";
import AuthContext from "../../Context/AuthContext";
import authHeader from "../../utils/authHeader";
import data2 from "../../data/dyson.json"
import magazalar from "../../data/dysonmagaza.json"
import Map from "../results/mapDyson";

export default function NewPlanPage({
  data = { advertisers: [] },
  initialBrandId = null,
  onCancel,
  onSaved,               
  saveUrl = "https://opt-api.vercel.app/NewPlan",  
}) {
  // markalar
  const brandOptions = useMemo(() => {
    const out = [];
    (data.advertisers || []).forEach((adv) =>
      (adv.brands || []).forEach((b) =>
        out.push({ brandId: b.id, label: `${adv.name} / ${b.name}` })
      )
    );
    return out;
  }, [data]);

  const makeEmptyRow = () => ({
    id: String(Date.now()) + Math.random().toString(36).slice(2, 8),
    address: "",
    startDate: "",
    endDate: "",
    period: 1,          // <- schema: required min 1
    unit: "CLP",        // <- enum: CLP|Billboard|Mupi|Megaboard|Other
    type: "Media",      // <- enum: Media|Production|Other
    unitNumber: 0,
    faces: 0,
    unitPrice: 0,
    vendor: "",
  });

  const [brandId, setBrandId] = useState(() => initialBrandId || brandOptions[0]?.brandId || "");
  const [name, setName] = useState("");            
  const [currency, setCurrency] = useState("TRY"); 
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [saving, setSaving] = useState(false);
  const history = useHistory();
  const { userData } = useContext(AuthContext);
  const combinedData = useMemo(() => {
    // dysonmagaza.json verilerini uygun formata çeviriyoruz
    const storesAsRaw = (Array.isArray(magazalar) ? magazalar : []).map((m, idx) => ({
      "Adres": m.name || `Mağaza #${idx + 1}`,
      "Liste Tür": "Mağaza",
      latitude: String(m.latitude),
      longitude: String(m.longitude),
      "SES": "Mağaza", // Mağazalar için özel SES değeri
    }));

    // dyson.json verilerini alıyoruz (zaten doğru formatta)
    const base = Array.isArray(data2) ? data2 : [];

    // İki veriyi birleştiriyoruz
    return [...base, ...storesAsRaw];
  }, []);

  useEffect(() => {
    if (!brandId && brandOptions[0]) setBrandId(brandOptions[0].brandId);
  }, [brandOptions, brandId]);

  // hesap: satır bütçesi ve toplam
  const rowBudget = (r) => {
    const p = Number(r.period) || 0;
    const n = Number(r.unitNumber) || 0;
    const f = Number(r.faces) || 0;
    const price = Number(r.unitPrice) || 0;
    return p * n * f * price;
  };
  const totals = rows.reduce((acc, r) => acc + rowBudget(r), 0);

  // satır işlemleri
  const updateRow = (id, patch) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, makeEmptyRow()]);
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r.id !== id));

  // notlar
  const addNote = () => {
    const t = noteDraft.trim();
    if (!t) return;
    setNotes((n) => [...n, t]);
    setNoteDraft("");
  };
  const removeNote = (i) => setNotes((n) => n.filter((_, idx) => idx !== i));

  // doğrulama (şemayı referans alır)
  const validate = () => {
    const errs = [];
    if (!name.trim()) errs.push("Plan adı (Campaign) gerekli.");
    rows.forEach((r, i) => {
      if (!r.address) errs.push(`Satır ${i + 1}: Address gerekli`);
      if (!r.startDate) errs.push(`Satır ${i + 1}: Start Date gerekli`);
      if (!r.endDate) errs.push(`Satır ${i + 1}: End Date gerekli`);
      if (r.startDate && r.endDate && new Date(r.endDate) < new Date(r.startDate)) {
        errs.push(`Satır ${i + 1}: End Date, Start Date'ten önce olamaz`);
      }
      if (!r.period || Number(r.period) < 1) errs.push(`Satır ${i + 1}: Period en az 1 olmalı`);
    });
    return errs;
  };

  // payload (Plan şemasına tam uyumlu)
  const buildPayload = () => ({
    name,
    brandId : userData.user.user.username ,
    currency,
    notes: notes.join("\n"),
    addresses: rows.map((r) => ({
      address: r.address,
      startDate: r.startDate ? new Date(r.startDate) : undefined,
      endDate: r.endDate ? new Date(r.endDate) : undefined,
      period: Number(r.period) || 1,
      unit: r.unit,
      type: r.type,
      unitNumber: Number(r.unitNumber) || 0,
      faces: Number(r.faces) || 0,
      unitPrice: Number(r.unitPrice) || 0, // backend Decimal128'e çeviriyor
      vendor: r.vendor,
    })),
  });

  // kaydet
  const handleSave = async () => {
    const errs = validate();
    if (errs.length) {
      alert("Lütfen hataları düzeltin:\n\n" + errs.join("\n"));
      return;
    }
    const payload = buildPayload();
    setSaving(true);
    try {
      const res = await axios.post(saveUrl, payload, authHeader());
      onSaved?.(res.data);
      alert("Plan kaydedildi.");
    } catch (err) {
      console.error(err);
      alert("Kaydetme sırasında hata: " + (err.response?.data?.hata || err.message || err));
    } finally {
      setSaving(false);
    }
  };



  const addressOptions = [
    "",
    "Taşkışla Caddesi Hayat Otel Karşısı Maçka Yönü",
    "Harbiye Mahallesi Kadırgalar Caddesi 3. Raket",
    "Göktürk İstanbul Caddesi 4. Raket Fidanlık Karşısı",
    "Kennedy Cad. Deniz Feneri Önü 1. Raket",
    "Hakkı Yeten Cad. Orta Ada Selenium Karşısı",
    "Tarlabaşı Bulvarı Şişhane Durağı Sonrası Taksim Yönü",
    "Atatürk Bulvarı Bağlantı Yönü Fatih İtfaiye Yönü",
    "Kennedy Cad. Gülhane Parkı Giriş Öncesi",
    "Kennedy Cad. Çatladı Kapı Durağı Sonrası Sultanahmet Girişi Havaalanı Yönü",
    "Halaskargazi Cad. Osmanbey İETT Durağı Yanı",
    "Ragıp Gümüşpala Cad. Eminönü Meydan-1",
    "T.Özal Millet Cd. Çapa İett Durağı Yanı Topkapı Yönü",
    "Cumhuriyet Cad. Beyoğlu 24. Noter Karşısı Orta Refüj",
    "T.Özal Millet Cd. Fındıkzade Şark Halı Önü",
    "Adnan Menderes Blv. İstanbul Valiliği Ek Hizmet Binası Önü",
    "Adnan Menderes Blv. Bezmialem Vakıf Ünv. Önü",
    "Dolmabahçe Gazhane Cad. Kadırgalar Cad. Birleşimi",
    "D-100 Bostancı Gidişi Honda Damla Önü-2",
    "Doğan Araslı Blv. Orta Refüj-6",
    "Halkalı Cd. Sefaköy D-100 Girişi-2"
  ];


  const unitOptions = ["CLP", "Billboard", "Megaboard", "Other"];
  const typeOptions = ["Media", "Production", "Other"];
  const currencyOptions = ["TRY", "USD", "EUR"];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 bg-white p-4 rounded shadow-sm">
        <button onClick={() => history.push("/homepage")} className="px-3 py-1 rounded border text-sm">‹ Geri</button>
        {/* <div>
          <h2 className="text-lg font-semibold">Yeni Plan</h2>
          <div className="text-sm text-slate-500">
            {brandOptions.find(b => b.brandId === brandId)?.label || "Marka seçiniz"}
          </div>
        </div> */}
        <div className="ms-auto flex items-center gap-3">
          <div className="text-sm text-slate-600">
            Toplam: <span className="font-medium">{tl(totals)}</span>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-4 rounded bg-brand-600 text-white hover:bg-brand-700"
          >
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </div>

      {/* Meta */}
      <div className="card p-4 bg-white rounded shadow-sm">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {/* <label className="block text-sm">
            <div className="text-slate-600 mb-1">Brand</div>
            <Select value={brandId} onChange={(e) => setBrandId(e.target.value)}>
              <option value="">-- Seçiniz --</option>
              {brandOptions.map((o) => (
                <option key={o.brandId} value={o.brandId}>{o.label}</option>
              ))}
            </Select>
          </label> */}

          <label className="block text-sm">
            <div className="text-slate-600 mb-1">Kampanya İsmi</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: İstanbul Kasım Outdoor" />
          </label>

          {/* <label className="block text-sm">
            <div className="text-slate-600 mb-1">Currency</div>
            <Select value={currency} onChange={(e) => setCurrency(e.target.value)}>
              {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </Select>
          </label> */}

          {/* <div className="block text-sm">
            <div className="text-slate-600 mb-1">Status</div>
            <div className="h-10 flex items-center"><Badge tone="brand">Draft</Badge></div>
          </div> */}
        </div>

        {/* Notlar (opsiyonel) */}
        {/* <div className="mt-4">
          <div className="text-slate-600 mb-2">Notlar</div>
          <div className="flex gap-2">
            <Input value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} placeholder="Not ekle…" />
            <button onClick={addNote} className="h-10 px-3 rounded border bg-white">Ekle</button>
          </div>
          {notes.length > 0 && (
            <ul className="mt-2 text-sm text-slate-700">
              {notes.map((n, i) => (
                <li key={i} className="flex items-center justify-between">
                  <span>{n}</span>
                  <button onClick={() => removeNote(i)} className="text-xs text-rose-600">sil</button>
                </li>
              ))}
            </ul>
          )}
        </div> */}
      </div>

      {/* Satırlar */}
      <div className="card p-0 overflow-hidden bg-white rounded shadow-sm">
        <div className="p-3 flex items-center justify-between">
          <div className="font-medium">Satırlar</div>
          <button onClick={addRow} className="h-9 px-3 rounded bg-emerald-600 text-white">+ Satır Ekle</button>
        </div>

        <div className="overflow-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead>
              <tr className="text-slate-600 bg-slate-50">
                {[
                  "Address", "Start", "End", "Period", "Unit", "Type",
                  "Unit Number", "Faces", "Price", "Budget", "Vendor", ""
                ].map(h => <th key={h} className="p-2 text-left">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.id} className={idx % 2 ? "bg-slate-50/50" : ""}>
                  <td className="p-2">
                    <Select value={r.address} onChange={e => updateRow(r.id, { address: e.target.value })}>
                      {addressOptions.map(a => <option key={a} value={a}>{a || "Adres seçiniz"}</option>)}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Input type="date" value={r.startDate} onChange={e => updateRow(r.id, { startDate: e.target.value })} />
                  </td>
                  <td className="p-2">
                    <Input type="date" value={r.endDate} onChange={e => updateRow(r.id, { endDate: e.target.value })} />
                  </td>
                  <td className="p-2">
                    <Input type="number" min={1} value={r.period} onChange={e => updateRow(r.id, { period: Number(e.target.value) })} />
                  </td>
                  <td className="p-2">
                    <Select value={r.unit} onChange={e => updateRow(r.id, { unit: e.target.value })}>
                      {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Select value={r.type} onChange={e => updateRow(r.id, { type: e.target.value })}>
                      {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  </td>
                  <td className="p-2">
                    <Input type="number" min={0} value={r.unitNumber} onChange={e => updateRow(r.id, { unitNumber: Number(e.target.value) })} />
                  </td>
                  <td className="p-2">
                    <Input type="number" min={0} value={r.faces} onChange={e => updateRow(r.id, { faces: Number(e.target.value) })} />
                  </td>
                  <td className="p-2">
                    <Input type="number" min={0} value={r.unitPrice} onChange={e => updateRow(r.id, { unitPrice: Number(e.target.value) })} />
                  </td>
                  <td className="p-2 font-medium">{tl(rowBudget(r))}</td>
                  <td className="p-2">
                    <Input value={r.vendor} onChange={e => updateRow(r.id, { vendor: e.target.value })} placeholder="Vendor" />
                  </td>
                  <td className="p-2">
                    <button onClick={() => removeRow(r.id)} className="text-rose-600">Sil</button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50">
                <td colSpan={9} className="text-right p-2 font-semibold">Total</td>
                <td className="p-2 font-semibold">{tl(totals)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
          <Map rawData={combinedData} zoom={12} />
        </div>
      </div>
    </div>
  );
}
